/**
 * Excel Export Utility for Frontend
 * Uses xlsx library for client-side Excel generation
 */

import * as XLSX from 'xlsx';

/**
 * Export data to Excel file
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Column definitions with {key, header} objects
 * @param {string} filename - Output filename
 * @param {string} sheetName - Sheet name
 */
export const exportToExcel = (data, columns, filename = 'export.xlsx', sheetName = 'Sheet1') => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // Create worksheet data
  const worksheetData = [];
  
  // Add header row
  const headers = columns.map(col => col.header);
  worksheetData.push(headers);
  
  // Add data rows
  data.forEach(row => {
    const rowData = columns.map(col => row[col.key] || '');
    worksheetData.push(rowData);
  });
  
  // Create workbook and worksheet
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  
  // Set column widths
  const colWidths = columns.map(col => ({ wch: col.width || 15 }));
  worksheet['!cols'] = colWidths;
  
  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  
  // Generate file and download
  XLSX.writeFile(workbook, filename);
};

/**
 * Export simulation results to Excel with unified bridge structure
 * Multiple sheets: Hierarchical Results, Summary, By Repair Type, By Level, Elements List
 * @param {Object} simulationResults - Simulation results object
 * @param {Object} hierarchy - Hierarchical structure from transformToHierarchy
 * @param {Object} bridge - Bridge object with level config
 * @param {boolean} showDecayPercent - Whether to include decay percentages
 * @param {string} filename - Output filename
 */
export const exportSimulationToExcel = (simulationResults, hierarchy = null, bridge = null, showDecayPercent = true, filename = null) => {
  const defaultFilename = `simulation_results_${Date.now()}.xlsx`;
  const outputFilename = filename || defaultFilename;
  
  const workbook = XLSX.utils.book_new();
  
  // Check if this is a Monte Carlo simulation
  const isMonteCarlo = simulationResults.mode === 'monte_carlo';
  
  // For Monte Carlo, we don't require yearlyResults
  if (!isMonteCarlo && (!simulationResults.yearlyResults || simulationResults.yearlyResults.length === 0)) {
    console.warn('No yearly results to export');
    return;
  }
  
  // Handle Monte Carlo export separately
  if (isMonteCarlo) {
    return exportMonteCarloToExcel(simulationResults, bridge, outputFilename);
  }

  const timeHorizon = simulationResults.timeHorizonYears || simulationResults.yearlyResults.length;
  const startYear = simulationResults.simulationStartYear != null && simulationResults.simulationStartYear > 0
    ? simulationResults.simulationStartYear
    : new Date().getFullYear();
  const allYears = [];
  for (let i = 1; i <= timeHorizon; i++) {
    allYears.push(startYear + i - 1);
  }

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (typeof amount === 'number') {
      return amount.toFixed(2);
    }
    return amount || '0.00';
  };

  // Helper function to recursively build hierarchical rows
  const buildHierarchicalRows = (node, level = 0, rows = []) => {
    const indent = '  '.repeat(level);
    const row = [
      indent + (node.name || 'Unnamed'),
      node.code || '',
      node.area ? parseFloat(node.area).toFixed(2) : '0.00'
    ];

    // Add cost columns for each year
    let rowTotal = 0;
    allYears.forEach(year => {
      const yearData = node.years?.find(y => y.year === year);
      const cost = yearData?.cost || 0;
      row.push(formatCurrency(cost));
      rowTotal += cost;
    });
    row.push(formatCurrency(rowTotal));

    // Add decay percentage columns if enabled
    if (showDecayPercent) {
      allYears.forEach(year => {
        const yearData = node.years?.find(y => y.year === year);
        const decay = yearData?.degradationPercent || 0;
        row.push(decay > 0 ? decay.toFixed(2) + '%' : '');
      });
    }

    rows.push(row);

    // Process children
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => {
        buildHierarchicalRows(child, level + 1, rows);
      });
    }

    return rows;
  };

  // SHEET 1: Hierarchical Results
  if (hierarchy) {
    const hierarchicalData = [];
    
    // Build header row
    const headerRow = ['Item Name', 'Code', 'Area (m²)'];
    allYears.forEach(year => {
      headerRow.push(year.toString());
    });
    headerRow.push('Total');
    if (showDecayPercent) {
      allYears.forEach(year => {
        headerRow.push(`Decay % ${year}`);
      });
    }
    hierarchicalData.push(headerRow);

    // Build data rows
    const rows = buildHierarchicalRows(hierarchy);
    hierarchicalData.push(...rows);

    const hierarchicalSheet = XLSX.utils.aoa_to_sheet(hierarchicalData);
    hierarchicalSheet['!cols'] = [
      { wch: 40 }, // Item Name
      { wch: 25 }, // Code
      { wch: 12 }, // Area
      ...Array(timeHorizon).fill({ wch: 12 }), // Year columns
      { wch: 12 }, // Total
      ...(showDecayPercent ? Array(timeHorizon).fill({ wch: 10 }) : []) // Decay % columns
    ];
    XLSX.utils.book_append_sheet(workbook, hierarchicalSheet, 'Hierarchical Results');
  }

  // SHEET 2: Summary
  const summaryData = [];
  summaryData.push(['Metric', 'Value']);
  if (bridge) {
    // Handle both property name formats (name/bridgeName and bridgeCode/bridge_code)
    const bridgeName = bridge.name || bridge.bridgeName || 'N/A';
    const bridgeCode = bridge.bridgeCode || bridge.bridge_code || 'N/A';
    summaryData.push(['Bridge Name', bridgeName]);
    summaryData.push(['Bridge Code', bridgeCode]);
  }
  summaryData.push(['Simulation Date', new Date().toLocaleDateString()]);
  summaryData.push(['Time Horizon (years)', timeHorizon]);
  if (simulationResults.summary) {
    summaryData.push(['Total Cost', formatCurrency(simulationResults.summary.totalCost || 0)]);
    summaryData.push(['NPV', formatCurrency(simulationResults.summary.npv || 0)]);
    summaryData.push(['Average Yearly Cost', formatCurrency(simulationResults.summary.avgYearlyCost || 0)]);
  }
  if (simulationResults.scenario) {
    summaryData.push(['Decay Threshold', (simulationResults.scenario.decayThreshold || 2.0) + '%']);
    summaryData.push(['Max Consecutive Touch-ups', simulationResults.scenario.maxConsecutiveTouchUps || 2]);
    summaryData.push(['Discount Rate', ((simulationResults.scenario.discountRate || 0.05) * 100).toFixed(2) + '%']);
  }

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  summarySheet['!cols'] = [{ wch: 30 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

  // SHEET 3: By Repair Type
  const repairTypeCosts = {};
  
  simulationResults.yearlyResults.forEach((yearResult, yearIndex) => {
    const year = yearIndex + 1;
    
    if (yearResult.spanResults) {
      yearResult.spanResults.forEach(spanResult => {
        const processes = [
          ...(spanResult.repairProcessesA || []),
          ...(spanResult.repairProcessesB || []),
          ...(spanResult.repairProcessesC || [])
        ];
        
        const totalRepairCost = (spanResult.costA || 0) + (spanResult.costB || 0) + (spanResult.costC || 0);
        
        if (processes.length > 0 && totalRepairCost > 0) {
          const costPerProcess = totalRepairCost / processes.length;
          
          processes.forEach(process => {
            const processName = process.processName || 'Unknown';
            if (!repairTypeCosts[processName]) {
              repairTypeCosts[processName] = {};
            }
            repairTypeCosts[processName][year] = (repairTypeCosts[processName][year] || 0) + costPerProcess;
          });
        } else if (totalRepairCost > 0) {
          const repairType = 'General Repair';
          if (!repairTypeCosts[repairType]) {
            repairTypeCosts[repairType] = {};
          }
          repairTypeCosts[repairType][year] = (repairTypeCosts[repairType][year] || 0) + totalRepairCost;
        }
      });
    }
  });

  const repairTypeData = [];
  const headerRow3 = ['Repair Type'];
  allYears.forEach(year => {
    headerRow3.push(year.toString());
  });
  headerRow3.push('Total');
  repairTypeData.push(headerRow3);

  const repairTypes = Object.keys(repairTypeCosts).sort();
  repairTypes.forEach(repairType => {
    const row = [repairType];
    let total = 0;
    
    for (let year = 1; year <= timeHorizon; year++) {
      const cost = repairTypeCosts[repairType][year] || 0;
      row.push(formatCurrency(cost));
      total += cost;
    }
    
    row.push(formatCurrency(total));
    repairTypeData.push(row);
  });

  // Add total row
  const totalRow = ['TOTAL'];
  let grandTotal = 0;
  for (let year = 1; year <= timeHorizon; year++) {
    const yearTotal = repairTypes.reduce((sum, type) => {
      return sum + (repairTypeCosts[type][year] || 0);
    }, 0);
    totalRow.push(formatCurrency(yearTotal));
    grandTotal += yearTotal;
  }
  totalRow.push(formatCurrency(grandTotal));
  repairTypeData.push(totalRow);

  const repairTypeSheet = XLSX.utils.aoa_to_sheet(repairTypeData);
  repairTypeSheet['!cols'] = [
    { wch: 20 },
    ...Array(timeHorizon).fill({ wch: 12 }),
    { wch: 12 }
  ];
  XLSX.utils.book_append_sheet(workbook, repairTypeSheet, 'By Repair Type');

  // SHEET 4: By Level (if hierarchy exists)
  if (hierarchy && bridge) {
    const levelConfig = bridge.levelLabels 
      ? (typeof bridge.levelLabels === 'string' ? JSON.parse(bridge.levelLabels) : bridge.levelLabels)
      : {};
    
    const levelCosts = {}; // { levelNumber: { year: cost } }

    const aggregateByLevel = (node) => {
      if (node.level && node.level > 0) {
        const levelNum = node.level;
        const levelLabel = levelConfig[`level${levelNum}`]?.label || `Level ${levelNum}`;
        
        if (!levelCosts[levelLabel]) {
          levelCosts[levelLabel] = {};
        }

        allYears.forEach(year => {
          const yearData = node.years?.find(y => y.year === year);
          const cost = yearData?.cost || 0;
          levelCosts[levelLabel][year] = (levelCosts[levelLabel][year] || 0) + cost;
        });
      }

      if (node.children) {
        node.children.forEach(child => {
          aggregateByLevel(child);
        });
      }
    };

    aggregateByLevel(hierarchy);

    const levelData = [];
    const headerRow4 = ['Level'];
    allYears.forEach(year => {
      headerRow4.push(year.toString());
    });
    headerRow4.push('Total');
    levelData.push(headerRow4);

    const levels = Object.keys(levelCosts).sort();
    levels.forEach(levelLabel => {
      const row = [levelLabel];
      let total = 0;
      
      for (let year = 1; year <= timeHorizon; year++) {
        const cost = levelCosts[levelLabel][year] || 0;
        row.push(formatCurrency(cost));
        total += cost;
      }
      
      row.push(formatCurrency(total));
      levelData.push(row);
    });

    const levelSheet = XLSX.utils.aoa_to_sheet(levelData);
    levelSheet['!cols'] = [
      { wch: 20 },
      ...Array(timeHorizon).fill({ wch: 12 }),
      { wch: 12 }
    ];
    XLSX.utils.book_append_sheet(workbook, levelSheet, 'By Level');
  }

  // SHEET 5: Elements List
  if (simulationResults.spans && simulationResults.spans.length > 0) {
    const elementsData = [];
    elementsData.push(['Element Code', 'Element Name', 'Area (m²)', 'Corrosion Category', 'Damage Level']);
    
    simulationResults.spans.forEach(span => {
      elementsData.push([
        span.spanCode || span.span_code || span.element_code || 'N/A',
        span.spanName || span.span_name || span.element_name || 'N/A',
        formatCurrency(span.totalArea || span.total_area || span.total_area || 0),
        span.corrosionCategory || span.corrosion_category || 'N/A',
        span.damageLevel || span.damage_level || 'N/A'
      ]);
    });

    const elementsSheet = XLSX.utils.aoa_to_sheet(elementsData);
    elementsSheet['!cols'] = [
      { wch: 30 }, // Element Code
      { wch: 30 }, // Element Name
      { wch: 15 }, // Area
      { wch: 20 }, // Corrosion Category
      { wch: 15 }  // Damage Level
    ];
    XLSX.utils.book_append_sheet(workbook, elementsSheet, 'Elements List');
  }
  
  // Download
  XLSX.writeFile(workbook, outputFilename);
};

/**
 * Export Monte Carlo simulation results to Excel
 * @param {Object} simulationResults - Monte Carlo simulation results object
 * @param {Object} bridge - Bridge object with level config
 * @param {string} filename - Output filename
 */
const exportMonteCarloToExcel = (simulationResults, bridge = null, filename) => {
  const workbook = XLSX.utils.book_new();
  
  // Helper function to extract numeric value (handles both numbers and objects)
  const getNumericValue = (value, defaultValue = 0) => {
    if (typeof value === 'number') {
      return value;
    }
    if (value && typeof value === 'object') {
      return value.mean || value.median || defaultValue;
    }
    return defaultValue;
  };
  
  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (typeof amount === 'number') {
      return amount.toFixed(2);
    }
    if (amount && typeof amount === 'object') {
      const numValue = getNumericValue(amount);
      return numValue.toFixed(2);
    }
    return '0.00';
  };
  
  const { statistics, distribution, deterministicComparison, iterations, timeHorizonYears, scenario } = simulationResults;
  
  // SHEET 1: Summary
  const summaryData = [];
  summaryData.push(['Metric', 'Value']);
  
  if (bridge) {
    // Handle both property name formats (name/bridgeName and bridgeCode/bridge_code)
    const bridgeName = bridge.name || bridge.bridgeName || 'N/A';
    const bridgeCode = bridge.bridgeCode || bridge.bridge_code || 'N/A';
    summaryData.push(['Bridge Name', bridgeName]);
    summaryData.push(['Bridge Code', bridgeCode]);
  }
  
  summaryData.push(['Simulation Date', new Date().toLocaleDateString()]);
  summaryData.push(['Simulation Type', 'Monte Carlo']);
  summaryData.push(['Iterations', iterations || 'N/A']);
  summaryData.push(['Time Horizon (years)', timeHorizonYears || 30]);
  
  if (scenario) {
    summaryData.push(['Decay Threshold', (scenario.decayThreshold || 2.0) + '%']);
    summaryData.push(['Max Consecutive Touch-ups', scenario.maxConsecutiveTouchUps || 2]);
    summaryData.push(['Discount Rate', ((scenario.discountRate || 0.05) * 100).toFixed(2) + '%']);
  }
  
  summaryData.push(['', '']); // Empty row separator
  
  // Total Cost Statistics
  if (statistics && statistics.totalCost) {
    summaryData.push(['Total Cost Statistics', '']);
    summaryData.push(['Mean', formatCurrency(statistics.totalCost.mean)]);
    summaryData.push(['Median (P50)', formatCurrency(statistics.totalCost.median)]);
    summaryData.push(['Minimum', formatCurrency(statistics.totalCost.min)]);
    summaryData.push(['Maximum', formatCurrency(statistics.totalCost.max)]);
    summaryData.push(['Standard Deviation', formatCurrency(statistics.totalCost.stdDev)]);
    summaryData.push(['P10 (10th Percentile)', formatCurrency(statistics.totalCost.p10)]);
    summaryData.push(['P25 (25th Percentile)', formatCurrency(statistics.totalCost.p25)]);
    summaryData.push(['P75 (75th Percentile)', formatCurrency(statistics.totalCost.p75)]);
    summaryData.push(['P90 (90th Percentile)', formatCurrency(statistics.totalCost.p90)]);
  }
  
  summaryData.push(['', '']); // Empty row separator
  
  // NPV Statistics
  if (statistics && statistics.npv) {
    summaryData.push(['NPV Statistics', '']);
    summaryData.push(['Mean', formatCurrency(statistics.npv.mean)]);
    summaryData.push(['Median (P50)', formatCurrency(statistics.npv.median)]);
    summaryData.push(['Minimum', formatCurrency(statistics.npv.min)]);
    summaryData.push(['Maximum', formatCurrency(statistics.npv.max)]);
    summaryData.push(['Standard Deviation', formatCurrency(statistics.npv.stdDev)]);
    summaryData.push(['P10 (10th Percentile)', formatCurrency(statistics.npv.p10)]);
    summaryData.push(['P25 (25th Percentile)', formatCurrency(statistics.npv.p25)]);
    summaryData.push(['P75 (75th Percentile)', formatCurrency(statistics.npv.p75)]);
    summaryData.push(['P90 (90th Percentile)', formatCurrency(statistics.npv.p90)]);
  }
  
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  summarySheet['!cols'] = [{ wch: 30 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
  
  // SHEET 2: Deterministic Comparison
  if (deterministicComparison) {
    const comparisonData = [];
    comparisonData.push(['Metric', 'Deterministic', 'Monte Carlo (Mean)', 'Difference', 'Difference %']);
    
    // Total Cost comparison
    const detTotalCost = deterministicComparison.totalCost || 0;
    const mcTotalCost = statistics?.totalCost?.mean || 0;
    const diffTotalCost = mcTotalCost - detTotalCost;
    const diffPercentTotalCost = detTotalCost !== 0 ? ((diffTotalCost / detTotalCost) * 100).toFixed(2) : '0.00';
    
    comparisonData.push([
      'Total Cost',
      formatCurrency(detTotalCost),
      formatCurrency(mcTotalCost),
      formatCurrency(diffTotalCost),
      diffPercentTotalCost + '%'
    ]);
    
    // NPV comparison
    const detNPV = deterministicComparison.npv || 0;
    const mcNPV = statistics?.npv?.mean || 0;
    const diffNPV = mcNPV - detNPV;
    const diffPercentNPV = detNPV !== 0 ? ((diffNPV / detNPV) * 100).toFixed(2) : '0.00';
    
    comparisonData.push([
      'NPV',
      formatCurrency(detNPV),
      formatCurrency(mcNPV),
      formatCurrency(diffNPV),
      diffPercentNPV + '%'
    ]);
    
    // Average Yearly Cost comparison
    if (deterministicComparison.avgYearlyCost !== undefined) {
      const detAvgYearly = deterministicComparison.avgYearlyCost || 0;
      const mcAvgYearly = mcTotalCost / (timeHorizonYears || 30);
      const diffAvgYearly = mcAvgYearly - detAvgYearly;
      const diffPercentAvgYearly = detAvgYearly !== 0 ? ((diffAvgYearly / detAvgYearly) * 100).toFixed(2) : '0.00';
      
      comparisonData.push([
        'Average Yearly Cost',
        formatCurrency(detAvgYearly),
        formatCurrency(mcAvgYearly),
        formatCurrency(diffAvgYearly),
        diffPercentAvgYearly + '%'
      ]);
    }
    
    const comparisonSheet = XLSX.utils.aoa_to_sheet(comparisonData);
    comparisonSheet['!cols'] = [
      { wch: 25 }, // Metric
      { wch: 18 }, // Deterministic
      { wch: 18 }, // Monte Carlo
      { wch: 18 }, // Difference
      { wch: 15 }  // Difference %
    ];
    XLSX.utils.book_append_sheet(workbook, comparisonSheet, 'Deterministic Comparison');
  }
  
  // SHEET 3: Total Cost Distribution
  if (distribution && distribution.totalCosts && distribution.totalCosts.length > 0) {
    const totalCostDistData = [];
    totalCostDistData.push(['Bin Range', 'Frequency', 'Percentage (%)', 'Cumulative %']);
    
    let cumulativeCount = 0;
    const totalCount = distribution.totalCosts.reduce((sum, bin) => sum + bin.count, 0);
    
    distribution.totalCosts.forEach(bin => {
      cumulativeCount += bin.count;
      const cumulativePercent = totalCount > 0 ? ((cumulativeCount / totalCount) * 100).toFixed(2) : '0.00';
      
      totalCostDistData.push([
        `${formatCurrency(bin.min)} - ${formatCurrency(bin.max)}`,
        bin.count,
        bin.percentage ? bin.percentage.toFixed(2) : '0.00',
        cumulativePercent
      ]);
    });
    
    const totalCostDistSheet = XLSX.utils.aoa_to_sheet(totalCostDistData);
    totalCostDistSheet['!cols'] = [
      { wch: 25 }, // Bin Range
      { wch: 12 }, // Frequency
      { wch: 15 }, // Percentage
      { wch: 15 }  // Cumulative %
    ];
    XLSX.utils.book_append_sheet(workbook, totalCostDistSheet, 'Total Cost Distribution');
  }
  
  // SHEET 4: NPV Distribution
  if (distribution && distribution.npv && distribution.npv.length > 0) {
    const npvDistData = [];
    npvDistData.push(['Bin Range', 'Frequency', 'Percentage (%)', 'Cumulative %']);
    
    let cumulativeCount = 0;
    const totalCount = distribution.npv.reduce((sum, bin) => sum + bin.count, 0);
    
    distribution.npv.forEach(bin => {
      cumulativeCount += bin.count;
      const cumulativePercent = totalCount > 0 ? ((cumulativeCount / totalCount) * 100).toFixed(2) : '0.00';
      
      npvDistData.push([
        `${formatCurrency(bin.min)} - ${formatCurrency(bin.max)}`,
        bin.count,
        bin.percentage ? bin.percentage.toFixed(2) : '0.00',
        cumulativePercent
      ]);
    });
    
    const npvDistSheet = XLSX.utils.aoa_to_sheet(npvDistData);
    npvDistSheet['!cols'] = [
      { wch: 25 }, // Bin Range
      { wch: 12 }, // Frequency
      { wch: 15 }, // Percentage
      { wch: 15 }  // Cumulative %
    ];
    XLSX.utils.book_append_sheet(workbook, npvDistSheet, 'NPV Distribution');
  }
  
  // SHEET 5: Sample Iterations
  if (simulationResults.sampleIterations && simulationResults.sampleIterations.length > 0) {
    const sampleIterationsData = [];
    sampleIterationsData.push(['Iteration', 'Total Cost', 'NPV', 'Notes']);
    
    simulationResults.sampleIterations.forEach((iteration, index) => {
      sampleIterationsData.push([
        index + 1,
        formatCurrency(iteration.totalCost || 0),
        formatCurrency(iteration.totalNPV || 0),
        'Sample iteration from Monte Carlo simulation'
      ]);
    });
    
    const sampleIterationsSheet = XLSX.utils.aoa_to_sheet(sampleIterationsData);
    sampleIterationsSheet['!cols'] = [
      { wch: 12 }, // Iteration
      { wch: 18 }, // Total Cost
      { wch: 18 }, // NPV
      { wch: 40 }  // Notes
    ];
    XLSX.utils.book_append_sheet(workbook, sampleIterationsSheet, 'Sample Iterations');
  }
  
  // SHEET 6: Elements List (if available)
  if (simulationResults.spans && simulationResults.spans.length > 0) {
    const elementsData = [];
    elementsData.push(['Element Code', 'Element Name', 'Area (m²)', 'Corrosion Category']);
    
    simulationResults.spans.forEach(span => {
      elementsData.push([
        span.spanCode || span.span_code || span.element_code || 'N/A',
        span.spanName || span.span_name || span.element_name || 'N/A',
        formatCurrency(span.totalArea || span.total_area || 0),
        span.corrosionCategory || span.corrosion_category || 'N/A'
      ]);
    });
    
    const elementsSheet = XLSX.utils.aoa_to_sheet(elementsData);
    elementsSheet['!cols'] = [
      { wch: 30 }, // Element Code
      { wch: 30 }, // Element Name
      { wch: 15 }, // Area
      { wch: 20 }  // Corrosion Category
    ];
    XLSX.utils.book_append_sheet(workbook, elementsSheet, 'Elements List');
  }
  
  // Download
  XLSX.writeFile(workbook, filename);
};

/**
 * Export repair project to Excel
 * @param {Object} projectData - Repair project data
 * @param {string} filename - Output filename
 */
export const exportRepairProjectToExcel = (projectData, filename = null) => {
  const defaultFilename = `repair_project_${projectData.id || Date.now()}.xlsx`;
  const outputFilename = filename || defaultFilename;
  
  const workbook = XLSX.utils.book_new();
  
  // Get level labels from bridge configuration
  const levelLabels = projectData.levelLabels || {};
  let areaDataLevelLabel = 'Elements';
  let childLevelLabel = 'Child Elements';
  
  // Find the level that holds area data
  for (const [levelKey, levelConfig] of Object.entries(levelLabels)) {
    if (levelConfig?.holdsAreaData) {
      areaDataLevelLabel = levelConfig.label || 'Elements';
    }
    if (levelConfig?.holdsChildData) {
      childLevelLabel = levelConfig.label || 'Child Elements';
    }
  }
  
  // Project Summary Sheet
  const summaryData = [];
  summaryData.push(['Field', 'Value']);
  summaryData.push(['Project Name', projectData.project_name || 'N/A']);
  summaryData.push(['Project Code', projectData.project_code || 'N/A']);
  summaryData.push(['Bridge', projectData.bridge_name || 'N/A']);
  summaryData.push(['Status', projectData.status || 'N/A']);
  summaryData.push(['Planned Year', projectData.planned_year || 'N/A']);
  summaryData.push(['Planned Start Date', projectData.planned_start_date || 'N/A']);
  summaryData.push(['Planned End Date', projectData.planned_end_date || 'N/A']);
  summaryData.push(['Actual Start Date', projectData.actual_start_date || 'N/A']);
  summaryData.push(['Actual End Date', projectData.actual_end_date || 'N/A']);
  summaryData.push(['Estimated Cost', projectData.estimated_cost || 0]);
  summaryData.push(['Actual Cost', projectData.actual_cost || 0]);
  summaryData.push(['Crews Count', projectData.crews_count || 0]);
  summaryData.push(['Duration (weeks)', projectData.duration_weeks || 'N/A']);
  summaryData.push(['Scaffolding Sites', projectData.scaffolding_sites_count || 0]);
  summaryData.push(['Scaffolding Area per Site (m²)', projectData.scaffolding_area_per_site || 0]);
  
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Project Summary');
  
  // Elements Sheet (area data level)
  const elements = projectData.elements || projectData.spans || [];
  if (elements.length > 0) {
    const elementsData = [];
    elementsData.push([
      `${areaDataLevelLabel} Code`, 
      `${areaDataLevelLabel} Name`, 
      'Total Area (m²)', 
      'Allocated Mobilization', 
      'Estimated Material Cost', 
      'Estimated Labor Cost', 
      'Total Estimated Cost',
      'Corrosion Category',
      'Damage Severity',
      'Status'
    ]);
    
    elements.forEach(element => {
      elementsData.push([
        element.element_code || element.span_code || element.spanCode || 'N/A',
        element.element_name || element.span_name || element.spanName || 'N/A',
        element.total_area || element.totalArea || element.span_total_area || 0,
        element.allocated_mobilization_cost || 0,
        element.estimated_material_cost || 0,
        element.estimated_labor_cost || 0,
        element.total_estimated_cost || 0,
        element.corrosion_category || 'N/A',
        element.damage_severity || 'N/A',
        element.element_status || element.span_status || element.status || 'pending'
      ]);
    });
    
    const elementsSheet = XLSX.utils.aoa_to_sheet(elementsData);
    XLSX.utils.book_append_sheet(workbook, elementsSheet, areaDataLevelLabel);
  }
  
  // Child Elements Sheet
  const allChildElements = [];
  elements.forEach(element => {
    const childElements = element.childElements || [];
    childElements.forEach(child => {
      allChildElements.push({
        parentCode: element.element_code || element.span_code || 'N/A',
        parentName: element.element_name || element.span_name || 'N/A',
        ...child
      });
    });
  });
  
  if (allChildElements.length > 0) {
    const childElementsData = [];
    childElementsData.push([
      `Parent ${areaDataLevelLabel} Code`,
      `Parent ${areaDataLevelLabel} Name`,
      `${childLevelLabel} Identifier`,
      'Average Area (m²)',
      'Estimated Damage Area (m²)',
      'Current Damage Area (m²)',
      'Damage Severity',
      'Corrosion Category',
      'From Inspection',
      'Paint Status',
      'Painted Area (m²)',
      'Paint Percentage (%)'
    ]);
    
    allChildElements.forEach(child => {
      childElementsData.push([
        child.parentCode || 'N/A',
        child.parentName || 'N/A',
        child.child_element_identifier || child.childElementIdentifier || 'N/A',
        child.average_area || child.averageArea || 0,
        child.estimated_damage_area || child.estimatedDamageArea || 0,
        child.current_damage_area || child.currentDamageArea || child.actual_damage_area || 0,
        child.damage_severity || child.damageSeverity || 'N/A',
        child.corrosion_category || child.corrosionCategory || 'N/A',
        child.from_inspection || child.fromInspection ? 'Yes' : 'No',
        child.paint_status || child.paintStatus || 'N/A',
        child.painted_area || child.paintedArea || 0,
        child.paint_percentage || child.paintPercentage || 0
      ]);
    });
    
    const childElementsSheet = XLSX.utils.aoa_to_sheet(childElementsData);
    XLSX.utils.book_append_sheet(workbook, childElementsSheet, childLevelLabel);
  }
  
  // Cost Breakdown Sheet
  const costData = [];
  costData.push(['Category', 'Estimated', 'Actual', 'Variance']);
  costData.push([
    'Material',
    projectData.estimated_material_cost || 0,
    projectData.actual_material_cost || 0,
    (projectData.actual_material_cost || 0) - (projectData.estimated_material_cost || 0)
  ]);
  costData.push([
    'Labor',
    projectData.estimated_labor_cost || 0,
    projectData.actual_labor_cost || 0,
    (projectData.actual_labor_cost || 0) - (projectData.estimated_labor_cost || 0)
  ]);
  costData.push([
    'Mobilization',
    projectData.estimated_mobilization_cost || 0,
    projectData.actual_mobilization_cost || 0,
    (projectData.actual_mobilization_cost || 0) - (projectData.estimated_mobilization_cost || 0)
  ]);
  
  const estimatedTotal = projectData.estimated_cost || 0;
  const actualTotal = projectData.actual_cost || 0;
  costData.push([
    'Total',
    estimatedTotal,
    actualTotal,
    actualTotal - estimatedTotal
  ]);
  
  const costSheet = XLSX.utils.aoa_to_sheet(costData);
  XLSX.utils.book_append_sheet(workbook, costSheet, 'Cost Breakdown');
  
  // Download
  XLSX.writeFile(workbook, outputFilename);
};

